// ============================================================================
// DESIGNATION SERVICE
// ============================================================================
// Business logic and HTTP calls for the Designation master.
//
// Backend wraps every response in ApiResponse<T> with this shape:
//   { success, message, data, errors, correlationId, timestamp }
//
// List endpoints additionally wrap data in PagedResult<T>:
//   data: { items, page, pageSize, totalCount, totalPages, hasPrevious, hasNext }
//
// The frontend grids (SyutGrid) consume PaginatedResponse<T> with shape:
//   { success, message, data: T[], pagination: { currentPage, pageSize, totalPages, totalItems, hasNextPage, hasPreviousPage } }
//
// `getAll()` MUST do this transformation — see CLAUDE.md "Service layer transformation".

import { get, post, put, del } from '../client/axiosClient';
import { designationEndpoints } from '../endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
  Designation,
  CreateDesignationRequest,
  UpdateDesignationRequest,
  DesignationSearchRequest,
} from '../types';

class DesignationService {
  /**
   * Get paginated list with optional filters.
   * Transforms backend PagedResult<T> → frontend PaginatedResponse<T>.
   */
  async getAll(
    params?: DesignationSearchRequest
  ): Promise<PaginatedResponse<Designation>> {
    const endpoint = params
      ? buildEndpointWithQuery(designationEndpoints.basePath(), params as Record<string, any>)
      : designationEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};

    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<Designation>> {
    const response = await get<ApiResponse<Designation>>(
      designationEndpoints.getById(id)
    );
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<Designation>> {
    const response = await get<ApiResponse<Designation>>(
      designationEndpoints.getByCode(code)
    );
    return response.data;
  }

  async create(
    data: CreateDesignationRequest
  ): Promise<ApiResponse<Designation>> {
    const response = await post<ApiResponse<Designation>>(
      designationEndpoints.create(),
      data
    );
    return response.data;
  }

  async update(
    id: ID,
    data: UpdateDesignationRequest
  ): Promise<ApiResponse<Designation>> {
    const response = await put<ApiResponse<Designation>>(
      designationEndpoints.update(id),
      data
    );
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    await del<void>(designationEndpoints.delete(id));
    return { success: true };
  }
}

export const designationService = new DesignationService();
export default designationService;
